import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useRef,
  useEffect,
  ReactNode,
} from "react";
import {
  getCommitmentsNew,
  getActionRequired,
  getFullCommitmentDetails,
} from "../services";
import {
  getInvitationsNew,
  getNMInvitationsNew,
  revokeOffer,
  revokeInvitationOfferNM,
  revokeInvitation,
} from "../services/memberService";
import { updateCommitmentAlt } from "../services/commitmentService";
import { updateInvitationAlt } from "../services/memberService";
import { useUserContext } from "./UserContext";
import {
  commitmentNeedsAnswer,
  belongsInNudgeRequestsTab,
  isTerminalForNudgeAnswering,
  isActionRequiredInvitationRow,
  assertIssuerCanIssueBadge,
  isActiveIssuerBadgeRequest,
  isAwaitingBadgeRerequestAfterClarify,
  isIssuerBadgeRequestVisible,
} from "../utils/notificationActionSync";
import { prefetchCommitmentModalData } from "../utils/commitmentModalData";
import {
  isOneShotPromiseBadge,
  isSlackBadgeCommitment,
  loadCommitmentForIssuerValidation,
  parseFullCommitmentRecord,
} from "../utils/badgeQuestions";
import { resolveCommitmentApiId } from "../utils/commitmentIds";

// Types
export interface Commitment {
  id: string | number;
  title: string;
  description: string;
  dueDate: string;
  committedDate: string;
  assignee: string;
  status:
    | "pending"
    | "approved"
    | "rejected"
    | "question"
    | "pending_approval"
    | "pending-acceptance"
    | "initiated"
    | "accepted"
    | "declined";
  type?: "nudge";
  nudgesLeft?: number;
  totalNudges?: number;
  nudgesToAnswer?: number;
  questions?: string[];
  responses?: unknown[];
  questionsAnswered?: unknown;
  questionsAsked?: unknown;
  extraData?: Record<string, unknown>;
  explanation?: string;
  isExternal?: boolean;
  reliabilityPoint?: number;
  badge?: { id: string; _id?: string; name?: string; [key: string]: any };
  completedDate?: string;
  committer?: { _id: string; [key: string]: any };
  issuer?: {
    _id: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    [key: string]: any;
  };
  promise?: string;
  commitmentDate?: string;
  _id?: string;
}

export interface CommitmentState {
  // Data
  myCommitments: Commitment[];
  receivedCommitments: Commitment[];
  actionRequired: Commitment[];
  // Invitations and NM data (stored as Commitment-like objects for consistency)
  receivedInvitations: any[]; // Invitations where user is committer (received invitations)
  sentInvitations: any[]; // Invitations where user is issuer (sent invitations)
  sentNMInvitations: any[]; // NM invitations sent by user
  sentNMOffers: any[]; // NM offers sent by user

  // Loading states
  loading: {
    myCommitments: boolean;
    receivedCommitments: boolean;
    actionRequired: boolean;
    invitations: boolean;
    nmData: boolean;
  };

  // Error states
  errors: {
    myCommitments: string | null;
    receivedCommitments: string | null;
    actionRequired: string | null;
    invitations: string | null;
    nmData: string | null;
  };

  // Cache management
  lastFetched: {
    myCommitments: number | null;
    receivedCommitments: number | null;
    actionRequired: number | null;
    invitations: number | null;
    nmData: number | null;
  };
  cacheTTL: number;
}

// Action types
type CommitmentAction =
  | { type: "FETCH_MY_COMMITMENTS_START" }
  | { type: "FETCH_MY_COMMITMENTS_SUCCESS"; payload: Commitment[] }
  | { type: "FETCH_MY_COMMITMENTS_ERROR"; payload: string }
  | { type: "FETCH_RECEIVED_COMMITMENTS_START" }
  | { type: "FETCH_RECEIVED_COMMITMENTS_SUCCESS"; payload: Commitment[] }
  | { type: "FETCH_RECEIVED_COMMITMENTS_ERROR"; payload: string }
  | { type: "FETCH_ACTION_REQUIRED_START" }
  | { type: "FETCH_ACTION_REQUIRED_SUCCESS"; payload: Commitment[] }
  | { type: "FETCH_ACTION_REQUIRED_ERROR"; payload: string }
  | { type: "FETCH_INVITATIONS_START" }
  | {
      type: "FETCH_INVITATIONS_SUCCESS";
      payload: { received: any[]; sent: any[] };
    }
  | { type: "FETCH_INVITATIONS_ERROR"; payload: string }
  | { type: "FETCH_NM_DATA_START" }
  | {
      type: "FETCH_NM_DATA_SUCCESS";
      payload: { invitations: any[]; offers: any[] };
    }
  | { type: "FETCH_NM_DATA_ERROR"; payload: string }
  | { type: "UPDATE_COMMITMENT"; payload: Commitment }
  | { type: "REMOVE_COMMITMENT"; payload: string | number }
  | { type: "ADD_MY_COMMITMENT"; payload: Commitment }
  | { type: "REMOVE_INVITATION"; payload: string | number }
  | { type: "ADD_INVITATION"; payload: any }
  | { type: "REMOVE_SENT_INVITATION"; payload: string | number }
  | { type: "ADD_SENT_INVITATION"; payload: any }
  | { type: "REMOVE_NM_INVITATION"; payload: string | number }
  | { type: "ADD_NM_INVITATION"; payload: any }
  | { type: "REMOVE_NM_OFFER"; payload: string | number }
  | { type: "ADD_NM_OFFER"; payload: any }
  | { type: "REMOVE_RECEIVED_COMMITMENT"; payload: string | number }
  | { type: "ADD_RECEIVED_COMMITMENT"; payload: Commitment }
  | { type: "CLEAR_ERROR"; payload: keyof CommitmentState["errors"] }
  | { type: "FORCE_REFRESH" }
  | { type: "CLEAR_ALL_DATA" };

// Initial state
const initialState: CommitmentState = {
  myCommitments: [],
  receivedCommitments: [],
  actionRequired: [],
  receivedInvitations: [],
  sentInvitations: [],
  sentNMInvitations: [],
  sentNMOffers: [],
  loading: {
    myCommitments: false,
    receivedCommitments: false,
    actionRequired: false,
    invitations: false,
    nmData: false,
  },
  errors: {
    myCommitments: null,
    receivedCommitments: null,
    actionRequired: null,
    invitations: null,
    nmData: null,
  },
  lastFetched: {
    myCommitments: null,
    receivedCommitments: null,
    actionRequired: null,
    invitations: null,
    nmData: null,
  },
  cacheTTL: 5 * 60 * 1000, // 5 minutes
};

// Reducer
const commitmentReducer = (
  state: CommitmentState,
  action: CommitmentAction
): CommitmentState => {
  switch (action.type) {
    case "FETCH_MY_COMMITMENTS_START":
      return {
        ...state,
        loading: { ...state.loading, myCommitments: true },
        errors: { ...state.errors, myCommitments: null },
      };
    case "FETCH_MY_COMMITMENTS_SUCCESS":
      return {
        ...state,
        loading: { ...state.loading, myCommitments: false },
        myCommitments: action.payload,
        lastFetched: {
          ...state.lastFetched,
          myCommitments: Date.now(),
        },
        errors: { ...state.errors, myCommitments: null },
      };
    case "FETCH_MY_COMMITMENTS_ERROR":
      return {
        ...state,
        loading: { ...state.loading, myCommitments: false },
        errors: { ...state.errors, myCommitments: action.payload },
      };
    case "FETCH_RECEIVED_COMMITMENTS_START":
      return {
        ...state,
        loading: { ...state.loading, receivedCommitments: true },
        errors: { ...state.errors, receivedCommitments: null },
      };
    case "FETCH_RECEIVED_COMMITMENTS_SUCCESS":
      return {
        ...state,
        loading: { ...state.loading, receivedCommitments: false },
        receivedCommitments: action.payload,
        lastFetched: {
          ...state.lastFetched,
          receivedCommitments: Date.now(),
        },
        errors: { ...state.errors, receivedCommitments: null },
      };
    case "FETCH_RECEIVED_COMMITMENTS_ERROR":
      return {
        ...state,
        loading: { ...state.loading, receivedCommitments: false },
        errors: { ...state.errors, receivedCommitments: action.payload },
      };
    case "FETCH_ACTION_REQUIRED_START":
      return {
        ...state,
        loading: { ...state.loading, actionRequired: true },
        errors: { ...state.errors, actionRequired: null },
      };
    case "FETCH_ACTION_REQUIRED_SUCCESS":
      return {
        ...state,
        loading: { ...state.loading, actionRequired: false },
        actionRequired: action.payload,
        lastFetched: {
          ...state.lastFetched,
          actionRequired: Date.now(),
        },
        errors: { ...state.errors, actionRequired: null },
      };
    case "FETCH_ACTION_REQUIRED_ERROR":
      return {
        ...state,
        loading: { ...state.loading, actionRequired: false },
        errors: { ...state.errors, actionRequired: action.payload },
      };
    case "FETCH_INVITATIONS_START":
      return {
        ...state,
        loading: { ...state.loading, invitations: true },
        errors: { ...state.errors, invitations: null },
      };
    case "FETCH_INVITATIONS_SUCCESS":
      return {
        ...state,
        loading: { ...state.loading, invitations: false },
        receivedInvitations: action.payload.received,
        sentInvitations: action.payload.sent,
        lastFetched: {
          ...state.lastFetched,
          invitations: Date.now(),
        },
        errors: { ...state.errors, invitations: null },
      };
    case "FETCH_INVITATIONS_ERROR":
      return {
        ...state,
        loading: { ...state.loading, invitations: false },
        errors: { ...state.errors, invitations: action.payload },
      };
    case "FETCH_NM_DATA_START":
      return {
        ...state,
        loading: { ...state.loading, nmData: true },
        errors: { ...state.errors, nmData: null },
      };
    case "FETCH_NM_DATA_SUCCESS":
      return {
        ...state,
        loading: { ...state.loading, nmData: false },
        sentNMInvitations: action.payload.invitations,
        sentNMOffers: action.payload.offers,
        lastFetched: {
          ...state.lastFetched,
          nmData: Date.now(),
        },
        errors: { ...state.errors, nmData: null },
      };
    case "FETCH_NM_DATA_ERROR":
      return {
        ...state,
        loading: { ...state.loading, nmData: false },
        errors: { ...state.errors, nmData: action.payload },
      };
    case "UPDATE_COMMITMENT": {
      const payloadKey = String(
        action.payload._id ?? action.payload.id ?? ""
      );
      const matches = (c: Commitment) => {
        const key = String(c._id ?? c.id ?? "");
        return (
          (payloadKey !== "" && key === payloadKey) ||
          (action.payload.id != null && c.id === action.payload.id)
        );
      };
      return {
        ...state,
        myCommitments: state.myCommitments.map((c) =>
          matches(c) ? { ...c, ...action.payload } : c
        ),
        receivedCommitments: state.receivedCommitments.map((c) =>
          matches(c) ? { ...c, ...action.payload } : c
        ),
        actionRequired: state.actionRequired.map((c) =>
          matches(c) ? { ...c, ...action.payload } : c
        ),
      };
    }
    case "REMOVE_COMMITMENT":
      return {
        ...state,
        myCommitments: state.myCommitments.filter(
          (c) => c.id !== action.payload && c._id !== action.payload
        ),
        receivedCommitments: state.receivedCommitments.filter(
          (c) => c.id !== action.payload && c._id !== action.payload
        ),
        actionRequired: state.actionRequired.filter(
          (c) => c.id !== action.payload && c._id !== action.payload
        ),
      };
    case "ADD_MY_COMMITMENT":
      return {
        ...state,
        myCommitments: [...state.myCommitments, action.payload],
      };
    case "REMOVE_INVITATION":
      return {
        ...state,
        receivedInvitations: state.receivedInvitations.filter(
          (inv) => (inv._id || inv.id) !== action.payload
        ),
      };
    case "ADD_INVITATION":
      return {
        ...state,
        receivedInvitations: [...state.receivedInvitations, action.payload],
      };
    case "REMOVE_NM_OFFER":
      return {
        ...state,
        sentNMOffers: state.sentNMOffers.filter(
          (o) => (o._id || o.id) !== action.payload
        ),
      };
    case "ADD_NM_OFFER":
      return {
        ...state,
        sentNMOffers: [...state.sentNMOffers, action.payload],
      };
    case "CLEAR_ERROR":
      return {
        ...state,
        errors: { ...state.errors, [action.payload]: null },
      };
    case "FORCE_REFRESH":
      return {
        ...state,
        lastFetched: {
          myCommitments: null,
          receivedCommitments: null,
          actionRequired: null,
          invitations: null,
          nmData: null,
        },
      };
    case "REMOVE_RECEIVED_COMMITMENT":
      return {
        ...state,
        receivedCommitments: state.receivedCommitments.filter(
          (c) => (c._id || c.id) !== action.payload
        ),
      };
    case "ADD_RECEIVED_COMMITMENT":
      return {
        ...state,
        receivedCommitments: [...state.receivedCommitments, action.payload],
      };
    case "CLEAR_ALL_DATA":
      return initialState;
    default:
      return state;
  }
};

// Context
interface CommitmentContextType {
  state: CommitmentState;
  fetchMyCommitments: (params?: any, force?: boolean) => Promise<void>;
  fetchReceivedCommitments: (params?: any, force?: boolean) => Promise<void>;
  fetchActionRequired: (params?: any, force?: boolean) => Promise<void>;
  fetchAllCommitments: (params?: any) => Promise<void>;
  updateCommitment: (commitment: Commitment) => void;
  removeCommitment: (commitmentId: string | number) => void;
  requestBadge: (
    commitment: Commitment,
    data: {
      explanation: string;
      hoursWorked: number;
      /** Public URL (or data URL) for optional explanation media. */
      mediaExplanation?: string;
    }
  ) => Promise<void>;
  acceptInvitation: (
    invitation: any,
    data: {
      promise: string;
      dueDate: string;
      /** Committer's chosen nudge count for p2p invitations (overrides badge default). */
      questionsToAnswer?: number | string[];
    }
  ) => Promise<void>;
  declineInvitation: (invitationId: string | number) => Promise<void>;
  revokeOffer: (commitmentId: string | number) => Promise<void>;
  revokeNMOffer: (offerId: string | number) => Promise<void>;
  revokeInvitation: (invitationId: string | number) => Promise<void>;
  revokeNMInvitation: (invitationId: string | number) => Promise<void>;
  issueBadge: (commitmentId: string | number) => Promise<void>;
  rejectBadge: (commitmentId: string | number) => Promise<void>;
  clarifyBadge: (
    commitmentId: string | number,
    clarificationText: string
  ) => Promise<void>;
  acceptBadgeRequest: (commitmentId: string | number) => Promise<void>;
  declineBadgeRequest: (commitmentId: string | number) => Promise<void>;
  answerQuestion: (
    commitment: Commitment | any,
    answerText: string
  ) => Promise<void>;
  clearError: (errorType: keyof CommitmentState["errors"]) => void;
  refreshAllData: (params?: any) => Promise<void>;
  clearAllData: () => void;
  isDataStale: (lastFetchedTime: number | null) => boolean;
  // Filter functions
  getCommitmentsByStatus: (status: Commitment["status"]) => Commitment[];
  getApprovedCommitments: () => Commitment[];
  getPendingCommitments: () => Commitment[];
  getRejectedCommitments: () => Commitment[];
  getPendingApprovalCommitments: () => Commitment[];
  getMyCommitmentsByStatus: (status: Commitment["status"]) => Commitment[];
  getReceivedCommitmentsByStatus: (
    status: Commitment["status"]
  ) => Commitment[];
  getMyPendingApprovalCommitments: () => Commitment[];
  getReceivedPendingApprovalCommitments: () => Commitment[];
  // Actions page specific filters
  getMyPromisesForActions: () => Commitment[];
  /** Committer must answer a nudge / p2p question (includes getActionRequired). */
  getNudgesToAnswerForActions: () => Commitment[];
  getPromisesOwedToMeForActions: () => Commitment[];
  getRequestsToCommitForActions: () => Commitment[]; // Invitations where user is committer
  getAwaitingResponseForActions: () => Commitment[]; // Offers/Invitations sent by user waiting for acceptance
  getBadgeRequestsForActions: () => Commitment[]; // Offers received by user (issuer) with pending-acceptance
  /** Issuer must approve badge after committer requested it (WhatsApp "Badge Request"); status pending_approval / pending-approval. */
  getPendingApprovalBadgeRequestsForActions: () => Commitment[];
  /** Committer submitted badge request; waiting for issuer approval (myCommitments pending_approval). */
  getMyPendingApprovalBadgeRequestsForActions: () => Commitment[];
  getAwaitingResponseOthersForActions: () => Commitment[]; // Same as getAwaitingResponseForActions (for Others section)
  // Badge-based filtering
  getCommitmentsByBadgeName: (badgeName: string) => Commitment[];
  getMyCommitmentsByBadgeName: (badgeName: string) => Commitment[];
  getReceivedCommitmentsByBadgeName: (badgeName: string) => Commitment[];
  getCommitmentsByBadgeType: (badgeType: string) => Commitment[];
  getAllBadgeNames: () => string[];
  getAllBadgeTypes: () => string[];
  // Badge counting functions
  getApprovedBadgeCounts: () => Record<string, number>;
  getApprovedBadgeCount: (badgeName: string) => number;
  // Computed properties
  allCommitments: Commitment[];
  approvedCommitments: Commitment[];
  pendingCommitments: Commitment[];
  rejectedCommitments: Commitment[];
  pendingApprovalCommitments: Commitment[];
  approvedBadgeCounts: Record<string, number>;
  // Fetch commitment details with extraData
  fetchCommitmentDetails: (commitmentId: string) => Promise<any>;
}

const CommitmentContext = createContext<CommitmentContextType | undefined>(
  undefined
);

const normalizeStatus = (status: unknown): string =>
  String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

const isFinalizedActionStatus = (status: unknown): boolean => {
  const normalized = normalizeStatus(status);
  return [
    "approved",
    "rejected",
    "reject",
    "declined",
    "decline",
    "denied",
    "deny",
    "accepted",
    "initiated",
  ].includes(normalized);
};

const normalizeApiId = (value: unknown): string => {
  if (!value) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  return raw.includes("~") ? raw.split("~").pop() || "" : raw;
};

const resolveInvitationActionId = (invitation: any): string => {
  const candidates: unknown[] = [
    invitation?._id,
    invitation?.id,
    invitation?.invitationId,
    invitation?.invitation_id,
    invitation?.dbId,
    invitation?.invitation?._id,
    invitation?.invitation?.id,
  ];
  for (const candidate of candidates) {
    const resolved = normalizeApiId(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const resolveInvitationMemberPhone = (invitation: any, fallbackPhone?: string) =>
  fallbackPhone ||
  invitation?.committer?.phone ||
  invitation?.invitee?.phone ||
  invitation?.member?.phone ||
  "";

const resolveQuestionsToAnswer = (
  invitation: any,
  provided?: number | string[]
) => {
  if (isOneShotPromiseBadge(invitation)) {
    return undefined;
  }

  // Direct number from UI (committer chose how many to answer).
  if (typeof provided === "number" && Number.isInteger(provided) && provided > 0) {
    return provided;
  }
  // Legacy: array of question texts whose length implies the count.
  if (Array.isArray(provided) && provided.length > 0) {
    return provided.length;
  }

  const candidates = [
    invitation?.questionsToAnswer,
    invitation?.questions_to_answer,
    invitation?.badge?.questionsToAnswer,
    invitation?.badge?.questions_to_answer,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }

  // Controller requires this for some p2p invitation accepts.
  return 1;
};

// Provider component
interface CommitmentProviderProps {
  children: ReactNode;
}

export const CommitmentProvider: React.FC<CommitmentProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(commitmentReducer, initialState);
  const { getUserId, isAuthenticated, phone } = useUserContext();
  const fetchAllInFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    const userId = getUserId();
    if (isAuthenticated && userId) {
      prefetchCommitmentModalData(userId);
    }
  }, [isAuthenticated, getUserId]);

  // Helper function to check if data is stale
  const isDataStale = useCallback(
    (lastFetchedTime: number | null) => {
      if (!lastFetchedTime) return true;
      return Date.now() - lastFetchedTime > state.cacheTTL;
    },
    [state.cacheTTL]
  );

  // Fetch my commitments
  const fetchMyCommitments = useCallback(
    async (params?: any, force: boolean = false) => {
      if (!force && !isDataStale(state.lastFetched.myCommitments)) {
        return;
      }

      if (!isAuthenticated) {
        return;
      }

      dispatch({ type: "FETCH_MY_COMMITMENTS_START" });
      try {
        const userId = getUserId();
        if (!userId) {
          throw new Error("User not authenticated");
        }
        const response = await getCommitmentsNew({
          committer: userId,
          ...params,
        });
        const payload =
          response.data?.results?.data ??
          response.data?.data ??
          (Array.isArray(response.data) ? response.data : []);
        dispatch({
          type: "FETCH_MY_COMMITMENTS_SUCCESS",
          payload: Array.isArray(payload) ? payload : [],
        });
      } catch (error) {
        dispatch({
          type: "FETCH_MY_COMMITMENTS_ERROR",
          payload: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [isDataStale, state.lastFetched.myCommitments, isAuthenticated, getUserId]
  );

  // Fetch received commitments
  const fetchReceivedCommitments = useCallback(
    async (params?: any, force: boolean = false) => {
      if (!force && !isDataStale(state.lastFetched.receivedCommitments)) {
        return;
      }

      if (!isAuthenticated) {
        return;
      }

      dispatch({ type: "FETCH_RECEIVED_COMMITMENTS_START" });
      try {
        const userId = getUserId();
        if (!userId) {
          throw new Error("User not authenticated");
        }
        const apiParams = {
          issuer: userId,
          ...params,
        };
        const response = await getCommitmentsNew(apiParams);
        const data = response.data?.results?.data || [];

        dispatch({
          type: "FETCH_RECEIVED_COMMITMENTS_SUCCESS",
          payload: data,
        });
      } catch (error) {
        dispatch({
          type: "FETCH_RECEIVED_COMMITMENTS_ERROR",
          payload: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [
      isDataStale,
      state.lastFetched.receivedCommitments,
      isAuthenticated,
      getUserId,
    ]
  );

  // Fetch action required
  const fetchActionRequired = useCallback(
    async (params?: any, force: boolean = false) => {
      if (!force && !isDataStale(state.lastFetched.actionRequired)) {
        return;
      }

      if (!isAuthenticated) {
        return;
      }

      dispatch({ type: "FETCH_ACTION_REQUIRED_START" });
      try {
        const response = await getActionRequired(params);
        const payload =
          response.data?.results?.data ??
          response.data?.data ??
          (Array.isArray(response.data) ? response.data : []);
        dispatch({
          type: "FETCH_ACTION_REQUIRED_SUCCESS",
          payload: Array.isArray(payload) ? payload : [],
        });
      } catch (error) {
        dispatch({
          type: "FETCH_ACTION_REQUIRED_ERROR",
          payload: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [isDataStale, state.lastFetched.actionRequired, isAuthenticated]
  );

  // Fetch invitations (received and sent)
  const fetchInvitations = useCallback(
    async (params?: any, force: boolean = false) => {
      if (!force && !isDataStale(state.lastFetched.invitations)) {
        return;
      }

      if (!isAuthenticated) {
        return;
      }

      dispatch({ type: "FETCH_INVITATIONS_START" });
      try {
        const userId = getUserId();
        if (!userId) {
          throw new Error("User not authenticated");
        }

        // Get timezone (using browser timezone as fallback)
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const [receivedResponse, sentResponse] = await Promise.all([
          // Received invitations where user is committer.
          getInvitationsNew({
            committer: userId,
            status: "pending",
            tz: timezone,
            ...params,
          }),
          // Sent invitations where user is issuer.
          getInvitationsNew({
            issuer: userId,
            status: "pending",
            tz: timezone,
            ...params,
          }),
        ]);

        // Extract data - handle different response structures
        const receivedData =
          receivedResponse.data?.results?.data ||
          receivedResponse.data?.data ||
          receivedResponse.data ||
          [];
        const sentData =
          sentResponse.data?.results?.data ||
          sentResponse.data?.data ||
          sentResponse.data ||
          [];

        dispatch({
          type: "FETCH_INVITATIONS_SUCCESS",
          payload: {
            received: receivedData,
            sent: sentData,
          },
        });
      } catch (error) {
        dispatch({
          type: "FETCH_INVITATIONS_ERROR",
          payload: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [isDataStale, state.lastFetched.invitations, isAuthenticated, getUserId]
  );

  // Fetch NM invitations and offers (sent by user)
  const fetchNMData = useCallback(
    async (params?: any, force: boolean = false) => {
      if (!force && !isDataStale(state.lastFetched.nmData)) {
        return;
      }

      if (!isAuthenticated) {
        return;
      }

      dispatch({ type: "FETCH_NM_DATA_START" });
      try {
        const userId = getUserId();
        if (!userId) {
          throw new Error("User not authenticated");
        }

        // Get timezone
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const [invitationsResponse, offersResponse] = await Promise.all([
          getNMInvitationsNew({
            inviter: userId,
            status: "pending",
            tz: timezone,
            requestType: "invitation",
            ...params,
          }),
          getNMInvitationsNew({
            inviter: userId,
            status: "pending",
            tz: timezone,
            requestType: "offer",
            ...params,
          }),
        ]);

        dispatch({
          type: "FETCH_NM_DATA_SUCCESS",
          payload: {
            invitations:
              invitationsResponse.data?.results?.data ||
              invitationsResponse.data?.data ||
              [],
            offers:
              offersResponse.data?.results?.data ||
              offersResponse.data?.data ||
              [],
          },
        });
      } catch (error) {
        dispatch({
          type: "FETCH_NM_DATA_ERROR",
          payload: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },
    [isDataStale, state.lastFetched.nmData, isAuthenticated, getUserId]
  );

  // Fetch all commitments (dedupe concurrent callers — dashboard + notifications, etc.)
  const fetchAllCommitments = useCallback(
    async (params?: any) => {
      if (fetchAllInFlightRef.current) {
        return fetchAllInFlightRef.current;
      }

      const run = Promise.all([
        fetchMyCommitments(params),
        fetchReceivedCommitments(params),
        fetchInvitations(params),
        fetchNMData(params),
        fetchActionRequired(params),
      ]).then(() => undefined);

      fetchAllInFlightRef.current = run;
      try {
        await run;
      } finally {
        if (fetchAllInFlightRef.current === run) {
          fetchAllInFlightRef.current = null;
        }
      }
    },
    [
      fetchMyCommitments,
      fetchReceivedCommitments,
      fetchInvitations,
      fetchNMData,
      fetchActionRequired,
    ]
  );

  // Update commitment
  const updateCommitment = useCallback((commitment: Commitment) => {
    dispatch({ type: "UPDATE_COMMITMENT", payload: commitment });
  }, []);

  // Remove commitment
  const removeCommitment = useCallback((commitmentId: string | number) => {
    dispatch({ type: "REMOVE_COMMITMENT", payload: commitmentId });
  }, []);

  // Request badge for a commitment
  const requestBadge = useCallback(
    async (
      commitment: Commitment,
      data: {
        explanation: string;
        hoursWorked: number;
        mediaExplanation?: string;
      }
    ) => {
      if (!commitment) {
        throw new Error(
          "Unable to load commitment details. Please refresh the page and try again."
        );
      }

      // Prioritize _id over id since API expects MongoDB ObjectId
      // MongoDB ObjectId is 24 hex characters, not a number
      let commitmentId: string | number | undefined;

      if (commitment._id) {
        commitmentId = commitment._id;
      } else if (commitment.id) {
        // If _id is missing, check if id is a valid ObjectId (24 hex chars)
        if (typeof commitment.id === "string" && commitment.id.length === 24) {
          commitmentId = commitment.id;
        } else {
          // id is a number or invalid format - we need _id
          throw new Error(
            "Something went wrong. Please refresh the page and try again."
          );
        }
      }

      if (!commitmentId) {
        throw new Error(
          "Unable to process this request. Please refresh the page and try again."
        );
      }

      // Get user phone (from context or commitment)
      const userPhone = phone || commitment.committer?.phone || "";

      if (!userPhone) {
        throw new Error(
          "Unable to verify your account. Please contact support if this continues."
        );
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const mediaExplanation = String(data.mediaExplanation || "").trim();
      const apiPayload = {
        member: userPhone,
        tz: timezone,
        explanation_text: data.explanation,
        input: data.explanation,
        source: "web",
        hoursWorked: data.hoursWorked,
        media_explanation: mediaExplanation,
      };

      // Optimistic update: Remove from myCommitments immediately
      dispatch({ type: "REMOVE_COMMITMENT", payload: commitmentId });

      try {
        // Call API
        const response = await updateCommitmentAlt(
          commitmentId.toString(),
          apiPayload as any
        );

        // Parse response
        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch myCommitments to sync with server
          // Commitment status changed, won't appear in "My Promises" anymore
          await fetchMyCommitments();
        } else {
          // API returned error - rollback
          // Parse API error and convert to user-friendly message
          const apiError = result?.errors || result?.error || "";
          let userFriendlyError =
            "Unable to submit your badge request. Please try again in a moment.";

          if (typeof apiError === "string") {
            // Check for common validation errors
            if (
              apiError.toLowerCase().includes("explanation") ||
              apiError.toLowerCase().includes("required")
            ) {
              userFriendlyError = "Your explanation is required.";
            } else if (
              apiError.toLowerCase().includes("hours") ||
              apiError.toLowerCase().includes("work")
            ) {
              userFriendlyError = "Hours worked must be greater than 0.";
            } else if (apiError.length > 0) {
              // For other API errors, use generic message
              userFriendlyError =
                "Unable to submit your badge request. Please try again in a moment.";
            }
          }

          throw new Error(userFriendlyError);
        }
      } catch (error) {
        // Rollback: Add commitment back to myCommitments
        dispatch({ type: "ADD_MY_COMMITMENT", payload: commitment });
        throw error;
      }
    },
    [phone, fetchMyCommitments]
  );

  // Accept invitation (creates commitment)
  const acceptInvitation = useCallback(
    async (
      invitation: any,
      data: {
        promise: string;
        dueDate: string;
        /** Committer's chosen nudge count for p2p invitations (overrides badge default). */
        questionsToAnswer?: number | string[];
      }
    ) => {
      if (!invitation) {
        throw new Error("Invitation not found");
      }

      const invitationId = resolveInvitationActionId(invitation);
      if (!invitationId) {
        throw new Error("Invitation ID not found");
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Prepare API payload
      const apiPayload: any = {
        status: "accepted",
        promise: data.promise || " ",
        source: "web",
        dueDate: data.dueDate.replace(/T/g, " "), // Format: "YYYY-MM-DD HH:mm"
        tz: timezone,
        // Legacy controller compatibility (some environments rely on these fields)
        member: resolveInvitationMemberPhone(invitation, phone),
        input: "accept",
      };

      // Recurring p2p invitations may require questionsToAnswer on accept.
      if (invitation.badge?.type === "p2p") {
        const questionsToAnswer = resolveQuestionsToAnswer(
          invitation,
          data.questionsToAnswer
        );
        if (
          typeof questionsToAnswer === "number" &&
          Number.isInteger(questionsToAnswer) &&
          questionsToAnswer > 0
        ) {
          apiPayload.questionsToAnswer = questionsToAnswer;
        }
      }

      // Optimistic update: Remove from receivedInvitations
      dispatch({ type: "REMOVE_INVITATION", payload: invitationId });

      try {
        const response = await updateInvitationAlt(
          invitationId,
          apiPayload
        );

        // Parse response
        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Reset cache to force refetch (bypasses stale check)
          dispatch({ type: "FORCE_REFRESH" });
          // Success: Refetch invitations and commitments
          // New commitment will appear in "My Promises"
          // Use force=true to bypass stale check after FORCE_REFRESH
          await Promise.all([
            fetchInvitations(undefined, true),
            fetchMyCommitments(undefined, true),
          ]);
        } else {
          throw new Error(
            result?.errors || result?.error || "Failed to accept invitation"
          );
        }
      } catch (error) {
        // Rollback: Add invitation back
        dispatch({ type: "ADD_INVITATION", payload: invitation });
        throw error;
      }
    },
    [fetchInvitations, fetchMyCommitments, phone]
  );

  // Decline invitation
  const declineInvitation = useCallback(
    async (invitationId: string | number) => {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Find invitation for rollback
      const invitation = state.receivedInvitations.find(
        (inv) =>
          resolveInvitationActionId(inv) === normalizeApiId(invitationId)
      );

      if (!invitation) {
        throw new Error("Invitation not found");
      }

      // Prepare API payload
      const apiPayload = {
        status: "declined",
        source: "web",
        tz: timezone,
        // Legacy controller compatibility
        member: resolveInvitationMemberPhone(invitation, phone),
        input: "decline",
      };

      // Optimistic update: Remove from receivedInvitations
      dispatch({ type: "REMOVE_INVITATION", payload: invitationId });

      try {
        const response = await updateInvitationAlt(
          resolveInvitationActionId(invitation),
          apiPayload
        );

        // Parse response
        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch invitations
          await fetchInvitations();
        } else {
          throw new Error(
            result?.errors || result?.error || "Failed to decline invitation"
          );
        }
      } catch (error) {
        // Rollback: Add invitation back
        dispatch({ type: "ADD_INVITATION", payload: invitation });
        throw error;
      }
    },
    [state.receivedInvitations, fetchInvitations, phone]
  );

  // Revoke offer (member offer with status="pending-acceptance")
  const revokeOfferAction = useCallback(
    async (commitmentId: string | number) => {
      // Find commitment for rollback
      const commitment = state.myCommitments.find(
        (c) => (c._id || c.id) === commitmentId
      );

      if (!commitment) {
        throw new Error("Commitment not found");
      }

      // Optimistic update: Remove from myCommitments
      dispatch({ type: "REMOVE_COMMITMENT", payload: commitmentId });

      try {
        const response = await revokeOffer({ id: commitmentId.toString() });

        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch to ensure consistency
          dispatch({ type: "FORCE_REFRESH" });
          await fetchMyCommitments(undefined, true);
        } else {
          // Rollback: Re-add commitment
          dispatch({ type: "ADD_MY_COMMITMENT", payload: commitment });
          throw new Error(
            result?.errors || result?.error || "Failed to revoke offer"
          );
        }
      } catch (error) {
        // Rollback: Re-add commitment
        dispatch({ type: "ADD_MY_COMMITMENT", payload: commitment });
        throw error;
      }
    },
    [state.myCommitments, fetchMyCommitments]
  );

  // Revoke non-member offer
  const revokeNMOfferAction = useCallback(
    async (offerId: string | number) => {
      // Find offer for rollback
      const offer = state.sentNMOffers.find((o) => (o._id || o.id) === offerId);

      if (!offer) {
        throw new Error("Offer not found");
      }

      // Optimistic update: Remove from sentNMOffers
      dispatch({ type: "REMOVE_NM_OFFER", payload: offerId });

      try {
        const response = await revokeInvitationOfferNM({
          id: offerId.toString(),
        });

        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch NM data
          dispatch({ type: "FORCE_REFRESH" });
          await fetchNMData(undefined, true);
        } else {
          // Rollback: Re-add offer
          dispatch({ type: "ADD_NM_OFFER", payload: offer });
          throw new Error(
            result?.errors || result?.error || "Failed to revoke offer"
          );
        }
      } catch (error) {
        // Rollback: Re-add offer
        dispatch({ type: "ADD_NM_OFFER", payload: offer });
        throw error;
      }
    },
    [state.sentNMOffers, fetchNMData]
  );

  // Revoke member invitation (sent by user, waiting for committer to accept)
  const revokeInvitationAction = useCallback(
    async (invitationId: string | number) => {
      // Find invitation for rollback
      const invitation = state.sentInvitations.find(
        (inv) => (inv._id || inv.id) === invitationId
      );

      if (!invitation) {
        throw new Error("Invitation not found");
      }

      // Optimistic update: Remove from sentInvitations
      dispatch({ type: "REMOVE_SENT_INVITATION", payload: invitationId });

      try {
        const response = await revokeInvitation({
          id: invitationId.toString(),
        });

        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch invitations
          dispatch({ type: "FORCE_REFRESH" });
          await fetchInvitations(undefined, true);
        } else {
          // Rollback: Re-add invitation
          dispatch({ type: "ADD_SENT_INVITATION", payload: invitation });
          throw new Error(
            result?.errors || result?.error || "Failed to revoke invitation"
          );
        }
      } catch (error) {
        // Rollback: Re-add invitation
        dispatch({ type: "ADD_SENT_INVITATION", payload: invitation });
        throw error;
      }
    },
    [state.sentInvitations, fetchInvitations]
  );

  // Revoke non-member invitation (sent by user, waiting for invitee to accept)
  const revokeNMInvitationAction = useCallback(
    async (invitationId: string | number) => {
      // Find invitation for rollback
      const invitation = state.sentNMInvitations.find(
        (inv) => (inv._id || inv.id) === invitationId
      );

      if (!invitation) {
        throw new Error("Invitation not found");
      }

      // Optimistic update: Remove from sentNMInvitations
      dispatch({ type: "REMOVE_NM_INVITATION", payload: invitationId });

      try {
        const response = await revokeInvitationOfferNM({
          id: invitationId.toString(),
        });

        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch NM data
          dispatch({ type: "FORCE_REFRESH" });
          await fetchNMData(undefined, true);
        } else {
          // Rollback: Re-add invitation
          dispatch({ type: "ADD_NM_INVITATION", payload: invitation });
          throw new Error(
            result?.errors || result?.error || "Failed to revoke invitation"
          );
        }
      } catch (error) {
        // Rollback: Re-add invitation
        dispatch({ type: "ADD_NM_INVITATION", payload: invitation });
        throw error;
      }
    },
    [state.sentNMInvitations, fetchNMData]
  );

  // Issue badge for a promise (user is issuer, approving the commitment)
  const issueBadge = useCallback(
    async (commitmentId: string | number) => {
      // Find commitment for rollback
      const commitment = state.receivedCommitments.find(
        (c) => (c._id || c.id) === commitmentId
      );

      if (!commitment) {
        throw new Error(
          "Unable to load commitment details. Please refresh the page and try again."
        );
      }

      const id = commitment._id || commitmentId;
      if (!id) {
        throw new Error(
          "Something went wrong. Please refresh the page and try again."
        );
      }

      // Use the latest server record for nudge completeness — list rows can be stale.
      const commitmentForCheck = await loadCommitmentForIssuerValidation(
        commitment
      );
      assertIssuerCanIssueBadge(commitmentForCheck);

      // Get issuer phone (user is issuer)
      const issuerPhone = phone || commitment.issuer?.phone || "";
      if (!issuerPhone) {
        throw new Error(
          "Unable to verify your account. Please contact support if this continues."
        );
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const apiPayload = {
        member: issuerPhone,
        input: "approve",
        source: "web",
        tz: timezone,
      };

      // Optimistic update: Remove from receivedCommitments
      dispatch({ type: "REMOVE_RECEIVED_COMMITMENT", payload: id });

      try {
        const response = await updateCommitmentAlt(
          id.toString(),
          apiPayload as any
        );

        // Parse response
        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch receivedCommitments to sync with server
          dispatch({ type: "FORCE_REFRESH" });
          await fetchReceivedCommitments(undefined, true);
        } else {
          // Rollback on API error
          dispatch({ type: "ADD_RECEIVED_COMMITMENT", payload: commitment });
          throw new Error(
            result?.errors ||
              result?.error ||
              "Unable to issue badge. Please try again."
          );
        }
      } catch (error) {
        // Rollback on network/other errors
        dispatch({ type: "ADD_RECEIVED_COMMITMENT", payload: commitment });
        throw error;
      }
    },
    [state.receivedCommitments, phone, fetchReceivedCommitments]
  );

  // Reject badge for a promise (user is issuer, rejecting the commitment)
  const rejectBadge = useCallback(
    async (commitmentId: string | number) => {
      // Find commitment for rollback
      const commitment = state.receivedCommitments.find(
        (c) => (c._id || c.id) === commitmentId
      );

      if (!commitment) {
        throw new Error(
          "Unable to load commitment details. Please refresh the page and try again."
        );
      }

      // Get commitment ID (prioritize _id for MongoDB ObjectId)
      const id = commitment._id || commitmentId;
      if (!id) {
        throw new Error(
          "Something went wrong. Please refresh the page and try again."
        );
      }

      // Get issuer phone (user is issuer)
      const issuerPhone = phone || commitment.issuer?.phone || "";
      if (!issuerPhone) {
        throw new Error(
          "Unable to verify your account. Please contact support if this continues."
        );
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const apiPayload = {
        member: issuerPhone,
        input: "reject",
        source: "web",
        tz: timezone,
      };

      // Optimistic update: Remove from receivedCommitments
      dispatch({ type: "REMOVE_RECEIVED_COMMITMENT", payload: id });

      try {
        const response = await updateCommitmentAlt(
          id.toString(),
          apiPayload as any
        );

        // Parse response
        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch receivedCommitments to sync with server
          dispatch({ type: "FORCE_REFRESH" });
          await fetchReceivedCommitments(undefined, true);
        } else {
          // Rollback on API error
          dispatch({ type: "ADD_RECEIVED_COMMITMENT", payload: commitment });
          throw new Error(
            result?.errors ||
              result?.error ||
              "Unable to reject badge. Please try again."
          );
        }
      } catch (error) {
        // Rollback on network/other errors
        dispatch({ type: "ADD_RECEIVED_COMMITMENT", payload: commitment });
        throw error;
      }
    },
    [state.receivedCommitments, phone, fetchReceivedCommitments]
  );

  // Accept a badge request (offer) - user is issuer accepting an offer to make a promise
  const acceptBadgeRequest = useCallback(
    async (commitmentId: string | number) => {
      // Find commitment for rollback
      const idStr = String(commitmentId);
      const commitment = state.receivedCommitments.find(
        (c) => String(c._id ?? c.id) === idStr
      );

      if (!commitment) {
        throw new Error(
          "Unable to load commitment details. Please refresh the page and try again."
        );
      }

      // Get commitment ID (prioritize _id for MongoDB ObjectId)
      const id = commitment._id ?? commitmentId;
      if (!id) {
        throw new Error(
          "Something went wrong. Please refresh the page and try again."
        );
      }

      // Get issuer phone (user is issuer)
      const issuerPhone = phone || commitment.issuer?.phone || "";
      if (!issuerPhone) {
        throw new Error(
          "Unable to verify your account. Please contact support if this continues."
        );
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const apiPayload = {
        member: issuerPhone,
        input: "accept",
        source: "web",
        tz: timezone,
      };

      // Optimistic update: Remove from receivedCommitments
      dispatch({ type: "REMOVE_RECEIVED_COMMITMENT", payload: id });

      try {
        const response = await updateCommitmentAlt(
          id.toString(),
          apiPayload as any
        );

        // Parse response
        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch receivedCommitments to sync with server
          dispatch({ type: "FORCE_REFRESH" });
          await fetchReceivedCommitments(undefined, true);
        } else {
          // Rollback on API error
          dispatch({ type: "ADD_RECEIVED_COMMITMENT", payload: commitment });
          throw new Error(
            result?.errors ||
              result?.error ||
              "Unable to accept badge request. Please try again."
          );
        }
      } catch (error) {
        // Rollback on network/other errors
        dispatch({ type: "ADD_RECEIVED_COMMITMENT", payload: commitment });
        throw error;
      }
    },
    [state.receivedCommitments, phone, fetchReceivedCommitments]
  );

  // Decline a badge request (offer) - user is issuer declining an offer to make a promise
  const declineBadgeRequest = useCallback(
    async (commitmentId: string | number) => {
      // Find commitment for rollback
      const idStr = String(commitmentId);
      const commitment = state.receivedCommitments.find(
        (c) => String(c._id ?? c.id) === idStr
      );

      if (!commitment) {
        throw new Error(
          "Unable to load commitment details. Please refresh the page and try again."
        );
      }

      // Get commitment ID (prioritize _id for MongoDB ObjectId)
      const id = commitment._id ?? commitmentId;
      if (!id) {
        throw new Error(
          "Something went wrong. Please refresh the page and try again."
        );
      }

      // Get issuer phone (user is issuer)
      const issuerPhone = phone || commitment.issuer?.phone || "";
      if (!issuerPhone) {
        throw new Error(
          "Unable to verify your account. Please contact support if this continues."
        );
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const apiPayload = {
        member: issuerPhone,
        input: "decline",
        source: "web",
        tz: timezone,
      };

      // Optimistic update: Remove from receivedCommitments
      dispatch({ type: "REMOVE_RECEIVED_COMMITMENT", payload: id });

      try {
        const response = await updateCommitmentAlt(
          id.toString(),
          apiPayload as any
        );

        // Parse response
        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          // Success: Refetch receivedCommitments to sync with server
          dispatch({ type: "FORCE_REFRESH" });
          await fetchReceivedCommitments(undefined, true);
        } else {
          // Rollback on API error
          dispatch({ type: "ADD_RECEIVED_COMMITMENT", payload: commitment });
          throw new Error(
            result?.errors ||
              result?.error ||
              "Unable to decline badge request. Please try again."
          );
        }
      } catch (error) {
        // Rollback on network/other errors
        dispatch({ type: "ADD_RECEIVED_COMMITMENT", payload: commitment });
        throw error;
      }
    },
    [state.receivedCommitments, phone, fetchReceivedCommitments]
  );

  // Clarify expectations for a promise (user is issuer, requesting clarification)
  const clarifyBadge = useCallback(
    async (commitmentId: string | number, clarificationText: string) => {
      // Validate clarification text
      if (!clarificationText || clarificationText.trim() === "") {
        throw new Error("Please enter your clarification.");
      }

      // Find commitment for reference
      const commitment = state.receivedCommitments.find(
        (c) => (c._id || c.id) === commitmentId
      );

      if (!commitment) {
        throw new Error(
          "Unable to load commitment details. Please refresh the page and try again."
        );
      }

      // Get commitment ID (prioritize _id for MongoDB ObjectId)
      const id = commitment._id || commitmentId;
      if (!id) {
        throw new Error(
          "Something went wrong. Please refresh the page and try again."
        );
      }

      // Get issuer phone (user is issuer)
      const issuerPhone = phone || commitment.issuer?.phone || "";
      if (!issuerPhone) {
        throw new Error(
          "Unable to verify your account. Please contact support if this continues."
        );
      }

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const apiPayload = {
        member: issuerPhone,
        input: "re-start",
        expectation: clarificationText.trim(),
        media_expectation: "", // Optional: can add S3 image upload later
        source: "web",
        tz: timezone,
      };

      const priorExpectations = [
        ...((commitment as any).extraData?.expectations ||
          (commitment as any).expectations ||
          []),
      ].filter((x) => String(x ?? "").trim());
      const nextExpectations = [
        ...priorExpectations,
        clarificationText.trim(),
      ];

      // Optimistic: issuer sent Clarify — row stays on Badge Requests until committer re-requests.
      dispatch({
        type: "UPDATE_COMMITMENT",
        payload: {
          ...commitment,
          expectations: nextExpectations,
          extraData: {
            ...((commitment as any).extraData || {}),
            expectations: nextExpectations,
          },
        } as Commitment,
      });

      try {
        const response = await updateCommitmentAlt(
          id.toString(),
          apiPayload as any
        );

        // Parse response
        let result;
        if (typeof response === "string") {
          result = JSON.parse(response);
        } else if (response?.data) {
          result =
            typeof response.data === "string"
              ? JSON.parse(response.data)
              : response.data;
        } else {
          result = response;
        }

        if (result?.success) {
          dispatch({ type: "FORCE_REFRESH" });
          await Promise.all([
            fetchReceivedCommitments(undefined, true),
            fetchMyCommitments(undefined, true),
          ]);
        } else {
          // Rollback optimistic expectations
          dispatch({ type: "UPDATE_COMMITMENT", payload: commitment });
          throw new Error(
            result?.errors ||
              result?.error ||
              "Unable to send clarification. Please try again."
          );
        }
      } catch (error) {
        dispatch({ type: "UPDATE_COMMITMENT", payload: commitment });
        throw error;
      }
    },
    [
      state.receivedCommitments,
      phone,
      fetchReceivedCommitments,
      fetchMyCommitments,
    ]
  );

  // Answer a p2p question for a commitment (committer submits answer text).
  const answerQuestion = useCallback(
    async (commitment: Commitment | any, answerText: string) => {
      const debugEnabled =
        typeof window !== "undefined" &&
        window.localStorage.getItem("rm_debug_questions") === "1";
      const trimmedAnswer = String(answerText || "").trim();
      if (!trimmedAnswer) {
        throw new Error("Please enter an answer before submitting.");
      }

      if (!commitment) {
        throw new Error(
          "Unable to load commitment details. Please refresh and try again."
        );
      }

      const initialId = resolveCommitmentApiId(commitment);
      if (!initialId) {
        throw new Error(
          "Unable to determine commitment id. Please refresh Actions and try again."
        );
      }

      // Always pull the freshest server snapshot before submitting. Modal
      // already does this on open, but the row may be stale by the time the
      // user finishes typing. This avoids 500s caused by an already-answered
      // or moved-on commitment, and gives us the canonical committer phone.
      let enriched: any = commitment;
      try {
        const detailRes = await getFullCommitmentDetails({
          _id: initialId,
          includeReminderAndLogs: true,
        });
        const record = parseFullCommitmentRecord(detailRes);
        if (record) {
          enriched = {
            ...commitment,
            ...record,
            _id: resolveCommitmentApiId(record) || initialId,
            badge: record.badge ?? commitment.badge,
            committer: record.committer ?? commitment.committer,
            issuer: record.issuer ?? commitment.issuer,
            extraData: {
              ...(commitment.extraData ?? {}),
              ...(record.extraData ?? {}),
            },
          };
        }
      } catch {
        // Network blip — fall back to whatever the caller passed in.
      }

      const commitmentId = resolveCommitmentApiId(enriched) || initialId;

      // Match legacy working flow: submit as the commitment committer first.
      const memberPhone = enriched?.committer?.phone || phone || "";
      if (!memberPhone) {
        throw new Error(
          "Unable to verify your account phone number for this action."
        );
      }

      const timezone =
        enriched?.committer?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      const apiPayload = {
        member: memberPhone,
        input: trimmedAnswer,
        source: "web",
        tz: timezone,
      };
      if (debugEnabled) {
        console.debug("[question-debug] submit payload", {
          commitmentId,
          payload: apiPayload,
          preSubmit: {
            status: enriched?.status,
            p2pState: enriched?.p2pState,
            currentQuestion: enriched?.currentQuestion,
          },
        });
      }

      let response: unknown;
      try {
        response = await updateCommitmentAlt(commitmentId, apiPayload as any);
      } catch (error: unknown) {
        const status = (error as { response?: { status?: number } })?.response
          ?.status;
        if (status === 500) {
          // 500 from /update/commitment typically means the commitment is no
          // longer accepting input (already answered, cancelled, or moved on).
          // Force a refresh so the user sees the latest state.
          dispatch({ type: "FORCE_REFRESH" });
          void fetchMyCommitments(undefined, true);
          throw new Error(
            "This nudge looks like it has already been answered or moved on. Actions has been refreshed — try again from the latest row, or answer via WhatsApp if the prompt is still there."
          );
        }
        const msg =
          error instanceof Error ? error.message : "Unable to submit your answer.";
        throw new Error(msg);
      }

      let result: any;
      if (typeof response === "string") {
        result = JSON.parse(response);
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response
      ) {
        const data = (response as { data: unknown }).data;
        result =
          typeof data === "string" ? JSON.parse(data) : data;
      } else {
        result = response;
      }

      if (!result?.success) {
        if (debugEnabled) {
          console.debug("[question-debug] submit failed response", result);
        }
        throw new Error(
          result?.errors ||
            result?.error ||
            "Unable to submit your answer. Please try again."
        );
      }
      if (debugEnabled) {
        console.debug("[question-debug] submit success response", result);
      }

      dispatch({ type: "FORCE_REFRESH" });
      await fetchMyCommitments(undefined, true);
      if (debugEnabled) {
        console.debug("[question-debug] forced myCommitments refresh complete");
      }
    },
    [phone, fetchMyCommitments]
  );

  // Clear error
  const clearError = useCallback(
    (errorType: keyof CommitmentState["errors"]) => {
      dispatch({ type: "CLEAR_ERROR", payload: errorType });
    },
    []
  );

  // Force refresh all data
  const refreshAllData = useCallback(
    async (params?: any) => {
      dispatch({ type: "FORCE_REFRESH" });
      await fetchAllCommitments(params);
    },
    [fetchAllCommitments]
  );

  // Clear all data
  const clearAllData = useCallback(() => {
    dispatch({ type: "CLEAR_ALL_DATA" });
  }, []);

  // Filter functions
  const getCommitmentsByStatus = useCallback(
    (status: Commitment["status"]) => {
      const allCommitments = [
        ...state.myCommitments,
        ...state.receivedCommitments,
      ];
      return allCommitments.filter(
        (commitment) => commitment.status === status
      );
    },
    [state.myCommitments, state.receivedCommitments]
  );

  const getApprovedCommitments = useCallback(() => {
    return getCommitmentsByStatus("approved");
  }, [getCommitmentsByStatus]);

  const getPendingCommitments = useCallback(() => {
    const allCommitments = [
      ...state.myCommitments,
      ...state.receivedCommitments,
    ];
    return allCommitments.filter(
      (commitment) =>
        ![
          "approved",
          "rejected",
          "pending-acceptance",
          "initiated",
          "accepted",
          "declined",
        ].includes(commitment.status)
    );
  }, [state.myCommitments, state.receivedCommitments]);

  const getRejectedCommitments = useCallback(() => {
    return getCommitmentsByStatus("rejected");
  }, [getCommitmentsByStatus]);

  const getPendingApprovalCommitments = useCallback(() => {
    return getCommitmentsByStatus("pending_approval");
  }, [getCommitmentsByStatus]);

  const getMyCommitmentsByStatus = useCallback(
    (status: Commitment["status"]) => {
      return state.myCommitments.filter(
        (commitment) => commitment.status === status
      );
    },
    [state.myCommitments]
  );

  const getReceivedCommitmentsByStatus = useCallback(
    (status: Commitment["status"]) => {
      return state.receivedCommitments.filter(
        (commitment) => commitment.status === status
      );
    },
    [state.receivedCommitments]
  );

  const getMyPendingApprovalCommitments = useCallback(() => {
    return state.myCommitments.filter(
      (commitment) => normalizeStatus(commitment.status) === "pending-approval"
    );
  }, [state.myCommitments]);

  const getReceivedPendingApprovalCommitments = useCallback(() => {
    return getReceivedCommitmentsByStatus("pending_approval");
  }, [getReceivedCommitmentsByStatus]);

  // Actions page specific filters
  const getNudgesToAnswerForActions = useCallback(() => {
    const seen = new Set<string>();
    const result: Commitment[] = [];
    const userId = getUserId();
    const pendingApprovalCommitmentIds = new Set<string>();
    for (const c of state.receivedCommitments) {
      if (normalizeStatus(c.status) !== "pending-approval") continue;
      const apiId = resolveCommitmentApiId(c);
      if (apiId) pendingApprovalCommitmentIds.add(apiId);
    }

    const nudgeTabOptions = {
      userId,
      pendingApprovalCommitmentIds,
    };

    const enrichActionRequiredRow = (c: Commitment): Commitment => {
      const apiId = resolveCommitmentApiId(c);
      if (!apiId) return c;
      const match =
        state.myCommitments.find(
          (m) => resolveCommitmentApiId(m) === apiId
        ) ??
        state.receivedCommitments.find(
          (m) => resolveCommitmentApiId(m) === apiId
        );
      if (!match) {
        return { ...(c as Commitment), _id: apiId } as Commitment;
      }
      return { ...match, ...c, _id: apiId } as Commitment;
    };

    const tryAdd = (
      c: Commitment,
      fromActionRequired = false,
      index = 0
    ) => {
      const row = fromActionRequired ? enrichActionRequiredRow(c) : c;
      const apiId = resolveCommitmentApiId(row);
      const dedupeKey =
        apiId ||
        String((row as { _id?: unknown; id?: unknown })._id ?? row.id ?? "").trim() ||
        `nudge-request-${fromActionRequired ? "ar" : "my"}-${index}`;
      if (seen.has(dedupeKey)) return;

      if (isTerminalForNudgeAnswering(row.status)) {
        return;
      }

      if (fromActionRequired && isActionRequiredInvitationRow(row)) {
        return;
      }

      // Promise Kept / Slack Promise Kept are one-shot — not Nudge Requests.
      if (isOneShotPromiseBadge(row as any)) return;
      // All Slack badges → My Promises + Request Badge (never Nudge Requests).
      if (isSlackBadgeCommitment(row as any)) return;

      if (
        !belongsInNudgeRequestsTab(row as any, {
          fromActionRequired,
          ...nudgeTabOptions,
        })
      ) {
        return;
      }

      if (!fromActionRequired && !commitmentNeedsAnswer(row as any)) return;

      seen.add(dedupeKey);
      result.push({
        ...(row as any),
        _id: apiId || (row as any)._id,
        _serverNeedsAnswer: fromActionRequired,
      } as Commitment);
    };

    const actionRequiredIds = new Set<string>();

    state.actionRequired.forEach((c, index) => {
      const enriched = enrichActionRequiredRow(c);
      const apiId = resolveCommitmentApiId(enriched);
      if (apiId) actionRequiredIds.add(apiId);
      tryAdd(c, true, index);
    });

    state.myCommitments.forEach((c, index) => {
      const apiId = resolveCommitmentApiId(c);
      const fromServer =
        (apiId && actionRequiredIds.has(apiId)) ||
        (c as { requiresAnswer?: boolean }).requiresAnswer === true ||
        (c as { actionRequired?: boolean }).actionRequired === true;
      tryAdd(c, fromServer, index);
    });
    return result;
  }, [
    state.myCommitments,
    state.receivedCommitments,
    state.actionRequired,
    getUserId,
  ]);

  const getMyPromisesForActions = useCallback(() => {
    const actionRequiredIds = new Set<string>();
    const userId = getUserId();
    const pendingApprovalCommitmentIds = new Set<string>();
    for (const c of state.receivedCommitments) {
      if (normalizeStatus(c.status) !== "pending-approval") continue;
      const apiId = resolveCommitmentApiId(c);
      if (apiId) pendingApprovalCommitmentIds.add(apiId);
    }
    const nudgeTabOptions = {
      userId,
      pendingApprovalCommitmentIds,
    };

    for (const c of state.actionRequired) {
      const apiId = resolveCommitmentApiId(c);
      if (apiId) actionRequiredIds.add(apiId);
    }

    return state.myCommitments.filter((c) => {
      // Exclude completed/final statuses and sent offers awaiting issuer response.
      const normalizedStatus = normalizeStatus(c.status);
      if (
        isFinalizedActionStatus(normalizedStatus) ||
        normalizedStatus === "pending-acceptance"
      ) {
        return false;
      }

      // Active badge requests (awaiting issuer) live on Awaiting Badge Approval.
      // After issuer Clarify, committer must Request Badge again — keep on My Promises.
      if (normalizedStatus === "pending-approval") {
        if (!isAwaitingBadgeRerequestAfterClarify(c)) {
          return false;
        }
      }

      const apiId = resolveCommitmentApiId(c);
      const fromActionRequired =
        (apiId && actionRequiredIds.has(apiId)) ||
        (c as { requiresAnswer?: boolean }).requiresAnswer === true ||
        (c as { actionRequired?: boolean }).actionRequired === true;

      // Keep recurring nudges on My Promises until a question is actually open
      // (Nudge Requests tab). Using commitmentNeedsAnswer alone hid accepted
      // onboarding promises between acceptance and the first nudge send.
      if (
        belongsInNudgeRequestsTab(c as any, {
          fromActionRequired,
          ...nudgeTabOptions,
        }) &&
        !isOneShotPromiseBadge(c as any) &&
        !isSlackBadgeCommitment(c as any)
      ) {
        return false;
      }

      return true;
    });
  }, [
    state.myCommitments,
    state.receivedCommitments,
    state.actionRequired,
    getUserId,
  ]);

  const getPromisesOwedToMeForActions = useCallback(() => {
    // Filter for commitments that should appear in "Promises Owed to Me"
    // Exclude finalized statuses, offers awaiting acceptance, and badge requests
    // awaiting issuer approval (those use the "Badge Requests" tab).
    const filtered = state.receivedCommitments.filter((c) => {
      const normalizedStatus = normalizeStatus(c.status);
      return (
        !isFinalizedActionStatus(normalizedStatus) &&
        normalizedStatus !== "pending-acceptance" &&
        normalizedStatus !== "pending-approval"
      );
    });

    return filtered;
  }, [state.receivedCommitments]);

  const getRequestsToCommitForActions = useCallback(() => {
    // Filter received invitations for pending status
    return state.receivedInvitations.filter((inv) => inv.status === "pending");
  }, [state.receivedInvitations]);

  const getAwaitingResponseForActions = useCallback(() => {
    // 1. Member offers sent by user (status="pending-acceptance")
    // User is committer (sent the offer), waiting for issuer to accept
    const sentMemberOffers = state.myCommitments.filter(
      (c) => normalizeStatus(c.status) === "pending-acceptance"
    );

    // 2. Non-member offers sent by user (status="pending")
    // User is issuer (sent the NM offer), waiting for invitee to accept
    const sentNMOffers = state.sentNMOffers.filter(
      (offer) => offer.status === "pending"
    );

    // Combine both sources
    return [...sentMemberOffers, ...sentNMOffers];
  }, [state.myCommitments, state.sentNMOffers]);

  const getBadgeRequestsForActions = useCallback(() => {
    return state.receivedCommitments.filter(
      (c) => normalizeStatus(c.status) === "pending-acceptance"
    );
  }, [state.receivedCommitments]);

  const getPendingApprovalBadgeRequestsForActions = useCallback(() => {
    return state.receivedCommitments.filter((c) =>
      isIssuerBadgeRequestVisible(c)
    );
  }, [state.receivedCommitments]);

  const getMyPendingApprovalBadgeRequestsForActions = useCallback(() => {
    return state.myCommitments.filter((c) => isActiveIssuerBadgeRequest(c));
  }, [state.myCommitments]);

  const getAwaitingResponseOthersForActions = useCallback(() => {
    const sentMemberInvitations = state.sentInvitations.filter(
      (inv) => inv.status === "pending"
    );

    const sentNMInvitations = state.sentNMInvitations.filter(
      (inv) => inv.status === "pending"
    );

    return [...sentMemberInvitations, ...sentNMInvitations];
  }, [state.sentInvitations, state.sentNMInvitations]);

  const getCommitmentsByBadgeName = useCallback(
    (badgeName: string) => {
      const allCommitments = [
        ...state.myCommitments,
        ...state.receivedCommitments,
      ];
      return allCommitments.filter(
        (commitment) => commitment.badge?.name === badgeName
      );
    },
    [state.myCommitments, state.receivedCommitments]
  );

  const getMyCommitmentsByBadgeName = useCallback(
    (badgeName: string) => {
      return state.myCommitments.filter(
        (commitment) => commitment.badge?.name === badgeName
      );
    },
    [state.myCommitments]
  );

  const getReceivedCommitmentsByBadgeName = useCallback(
    (badgeName: string) => {
      return state.receivedCommitments.filter(
        (commitment) => commitment.badge?.name === badgeName
      );
    },
    [state.receivedCommitments]
  );

  const getCommitmentsByBadgeType = useCallback(
    (badgeType: string) => {
      const allCommitments = [
        ...state.myCommitments,
        ...state.receivedCommitments,
      ];
      return allCommitments.filter(
        (commitment) => commitment.badge?.type === badgeType
      );
    },
    [state.myCommitments, state.receivedCommitments]
  );

  const getAllBadgeNames = useCallback(() => {
    const allCommitments = [
      ...state.myCommitments,
      ...state.receivedCommitments,
    ];
    const badgeNames = allCommitments
      .map((commitment) => commitment.badge?.name)
      .filter((name): name is string => !!name);
    return [...new Set(badgeNames)]; // Remove duplicates
  }, [state.myCommitments, state.receivedCommitments]);

  const getAllBadgeTypes = useCallback(() => {
    const allCommitments = [
      ...state.myCommitments,
      ...state.receivedCommitments,
    ];
    const badgeTypes = allCommitments
      .map((commitment) => commitment.badge?.type)
      .filter((type): type is string => !!type);
    return [...new Set(badgeTypes)]; // Remove duplicates
  }, [state.myCommitments, state.receivedCommitments]);

  // Badge counting functions for approved commitments (only where user is committer)
  const getApprovedBadgeCounts = useCallback(() => {
    const counts: Record<string, number> = {};

    state.myCommitments
      .filter((commitment) => commitment.status === "approved")
      .forEach((commitment) => {
        const badgeName = commitment.badge?.name;
        if (badgeName) {
          counts[badgeName] = (counts[badgeName] || 0) + 1;
        }
      });

    return counts;
  }, [state.myCommitments]);

  const getApprovedBadgeCount = useCallback(
    (badgeName: string) => {
      const counts = getApprovedBadgeCounts();
      return counts[badgeName] || 0;
    },
    [getApprovedBadgeCounts]
  );

  // Fetch commitment details with extraData (lazy loading, no caching)
  const fetchCommitmentDetails = useCallback(async (commitmentId: string) => {
    try {
      const response = await getFullCommitmentDetails({
        _id: commitmentId,
        includeReminderAndLogs: true,
      });
      return parseFullCommitmentRecord(response);
    } catch (error) {
      console.error(
        "[CommitmentContext] Error fetching commitment details:",
        error
      );
      throw error;
    }
  }, []);

  // Computed properties
  const allCommitments = useMemo(() => {
    return [...state.myCommitments, ...state.receivedCommitments];
  }, [state.myCommitments, state.receivedCommitments]);

  const approvedCommitments = useMemo(() => {
    return getApprovedCommitments();
  }, [getApprovedCommitments]);

  const pendingCommitments = useMemo(() => {
    return getPendingCommitments();
  }, [getPendingCommitments]);

  const rejectedCommitments = useMemo(() => {
    return getRejectedCommitments();
  }, [getRejectedCommitments]);

  const pendingApprovalCommitments = useMemo(() => {
    return getPendingApprovalCommitments();
  }, [getPendingApprovalCommitments]);

  // Computed approved badge counts
  const approvedBadgeCounts = useMemo(() => {
    return getApprovedBadgeCounts();
  }, [getApprovedBadgeCounts]);

  const value: CommitmentContextType = {
    state,
    fetchMyCommitments,
    fetchReceivedCommitments,
    fetchActionRequired,
    fetchAllCommitments,
    updateCommitment,
    removeCommitment,
    requestBadge,
    acceptInvitation,
    declineInvitation,
    revokeOffer: revokeOfferAction,
    revokeNMOffer: revokeNMOfferAction,
    revokeInvitation: revokeInvitationAction,
    revokeNMInvitation: revokeNMInvitationAction,
    issueBadge,
    rejectBadge,
    clarifyBadge,
    acceptBadgeRequest,
    declineBadgeRequest,
    answerQuestion,
    clearError,
    refreshAllData,
    clearAllData,
    isDataStale,
    // Filter functions
    getCommitmentsByStatus,
    getApprovedCommitments,
    getPendingCommitments,
    getRejectedCommitments,
    getPendingApprovalCommitments,
    getMyCommitmentsByStatus,
    getReceivedCommitmentsByStatus,
    getMyPendingApprovalCommitments,
    getReceivedPendingApprovalCommitments,
    getMyPromisesForActions,
    getNudgesToAnswerForActions,
    getPromisesOwedToMeForActions,
    getRequestsToCommitForActions,
    getAwaitingResponseForActions,
    getBadgeRequestsForActions,
    getPendingApprovalBadgeRequestsForActions,
    getMyPendingApprovalBadgeRequestsForActions,
    getAwaitingResponseOthersForActions,
    // Badge-based filtering
    getCommitmentsByBadgeName,
    getMyCommitmentsByBadgeName,
    getReceivedCommitmentsByBadgeName,
    getCommitmentsByBadgeType,
    getAllBadgeNames,
    getAllBadgeTypes,
    // Badge counting functions
    getApprovedBadgeCounts,
    getApprovedBadgeCount,
    // Computed properties
    allCommitments,
    approvedCommitments,
    pendingCommitments,
    rejectedCommitments,
    pendingApprovalCommitments,
    approvedBadgeCounts,
    fetchCommitmentDetails,
  };

  return (
    <CommitmentContext.Provider value={value}>
      {children}
    </CommitmentContext.Provider>
  );
};

// Hook to use the context
export const useCommitmentContext = (): CommitmentContextType => {
  const context = useContext(CommitmentContext);
  if (context === undefined) {
    throw new Error(
      "useCommitmentContext must be used within a CommitmentProvider"
    );
  }
  return context;
};
